import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

interface ProductPayload {
    name: string;
    description: string;
    sku: string;
    slug: string | null;
    status: 'active' | 'inactive';
    price: number;
    category_id: string;
    properties: {
        benefit?: string[];
    };
}

interface Product {
    id: string;
    name: string;
    description: string;
    sku: string;
    slug: string | null;
    status: 'active' | 'inactive';
    price: number;
    category_id: string;
    properties: any[];
}

interface Category {
    id: string;
    name: string;
}

interface Pagination {
    current_page: number;
    per_page: number;
    total: number;
    total_page: number;
}

@Component({
    selector: 'app-product',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './product.component.html',
    styleUrl: './product.component.scss'
})
export class ProductComponent {
    isModalOpen = false;
    isModalBenefitOpen = false;
    isEditMode = false;
    isSubmitting = false;

    Product: Product[] = [];
    Category: Category[] = [];
    pagination: Pagination = {
        current_page: 1,
        per_page: 100,
        total: 0,
        total_page: 1
    };

    productForm: ProductPayload = {
        name: '',
        description: '',
        sku: '',
        slug: null,
        status: 'inactive',
        price: 0,
        category_id: '',
        properties: {
            benefit: []
        }
    };

    selectedProductId: string = '';
    selectedProduct: Product | null = null;

    constructor(
        private apiService: ApiService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.getProducts();
        this.getCategories();
    }

    // ==================== CRUD OPERATIONS ====================

    // GET - List Products
    async getProducts(): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/tenant/manage/product/list?page=${this.pagination.current_page}&per_page=${this.pagination.per_page}`
            );
            // console.log(response);
            this.Product = response.data.list;
            this.pagination = response.data.pagination;
        } catch (error) {
            console.error('Error fetching products:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat memuat data product',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // GET - Single Product (untuk edit)
    async getProductById(productId: string): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/tenant/manage/product/${productId}`
            );

            // console.log('Product Details:', response);

            // Populate form dengan data product
            this.productForm = {
                name: response.data.name,
                description: response.data.description,
                sku: response.data.sku,
                slug: response.data.slug,
                status: response.data.status,
                price: response.data.price,
                category_id: response.data.category.id,
                properties: {
                    benefit: response.data.properties?.benefit || []  // Handle nested structure
                }
            };

            this.selectedProductId = productId;
            this.isEditMode = true;
            this.isModalOpen = true;
        } catch (error) {
            console.error('Error fetching product:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Gagal memuat data product',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // Add new benefit item
    addBenefit(): void {
        if (!this.productForm.properties.benefit) {
            this.productForm.properties.benefit = [];
        }
        this.productForm.properties.benefit.push('');
    }
    // Remove benefit item by index
    removeBenefit(index: number): void {
        this.productForm.properties.benefit.splice(index, 1);
    }
    // Get benefits array (helper method)
    getBenefits(): string[] {
        return this.productForm.properties.benefit || [];
    }

    // POST - Create Product
    async createProduct(): Promise<void> {
        if (!this.validateProductForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.post('/api/v1/tenant/manage/product', this.productForm);

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Product berhasil ditambahkan',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getProducts();
        } catch (error: any) {
            console.error('Error creating product:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal menambahkan product',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // PUT - Update Product
    async updateProduct(): Promise<void> {
        if (!this.validateProductForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.put(
                `/api/v1/tenant/manage/product/${this.selectedProductId}`,
                this.productForm
            );

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Product berhasil diupdate',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getProducts(); // Refresh list
        } catch (error: any) {
            console.error('Error updating product:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal mengupdate product',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // DELETE - Delete Product
    async deleteProduct(productId: string, productName: string): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Hapus',
            text: `Apakah Anda yakin ingin menghapus product "${productName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
        });

        if (result.isConfirmed) {
            try {
                await this.apiService.delete('/api/v1/tenant/manage/product', productId);

                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Product berhasil dihapus',
                    confirmButtonColor: '#28a745',
                    timer: 2000,
                    showConfirmButton: false,
                });

                this.getProducts(); // Refresh list
            } catch (error: any) {
                console.error('Error deleting product:', error);
                await Swal.fire({
                    icon: 'error',
                    title: 'Gagal',
                    text: error?.error?.message || 'Gagal menghapus product',
                    confirmButtonColor: '#dc3545',
                });
            }
        }
    }

    // ==================== MODAL OPERATIONS ====================

    openModalBenefit(product: Product): void {
        this.selectedProduct = product;
        this.isModalBenefitOpen = true;
    }

    closeModalBenefit(): void {
        this.isModalBenefitOpen = false;
    }

    openAddModal(): void {
        this.isEditMode = false;
        this.resetForm();
        this.isModalOpen = true;
    }

    openEditModal(productId: string): void {
        this.getProductById(productId);
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.resetForm();
    }

    resetForm(): void {
        this.productForm = {
            name: '',
            description: '',
            sku: '',
            slug: null,
            status: 'inactive',
            price: 0,
            category_id: '',
            properties: {
                benefit: [],
            }
        };
        this.selectedProductId = '';
    }

    // ==================== FORM VALIDATION ====================

    // GET - List Categories
    async getCategories(): Promise<void> {
        try {
            const response = await this.apiService.get(
                '/api/v1/tenant/manage/category/list?page=1&per_page=100'
            );
            this.Category = response.data.list;
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    }

    validateProductForm(): boolean {
        if (!this.productForm.name || !this.productForm.description) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Name dan Description wajib diisi',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        if (!this.productForm.sku) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'SKU wajib diisi',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        if (!this.productForm.category_id) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Category wajib dipilih',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        if (this.productForm.price < 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Price tidak boleh negatif',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        return true;
    }

    // ==================== PAGINATION ====================

    async goToPage(page: number): Promise<void> {
        if (page < 1 || page > this.pagination.total_page) {
            return;
        }
        this.pagination.current_page = page;
        await this.getProducts();
    }

    async nextPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page + 1);
    }

    async previousPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page - 1);
    }

    // ==================== HANDLER SUBMIT ====================

    async onSubmit(): Promise<void> {
        if (this.isEditMode) {
            await this.updateProduct();
        } else {
            await this.createProduct();
        }
    }

    // ==================== HELPER METHODS ====================

    // TrackBy function to prevent input focus loss
    trackByIndex(index: number): number {
        return index;
    }

    // Format number to Indonesian Rupiah format
    formatCurrency(value: number): string {
        if (!value && value !== 0) return '';
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    // Parse formatted currency back to number
    parseCurrency(value: string): number {
        if (!value) return 0;
        return parseInt(value.replace(/\./g, ''), 10) || 0;
    }
}
